// src/components/ClienteFormulariosTab.tsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Box,
  Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { getFormulariosUsuario, type Formulario } from '../api/formularios';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface Props {
  clienteId: number;
}

interface FormularioUsuario {
  id: number;
  titulo: string;
  tipoFormulario?: string;
  respuestasAnonimas?: boolean;
  respuestas: Array<{
    pregunta: string;
    respuesta: string;
    respondidoPor: string;
    fecha: string;
  }>;
  fecha: string;
}

export default function ClienteFormulariosTab({ clienteId }: Props) {
  const [formularios, setFormularios] = useState<FormularioUsuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFormulario, setSelectedFormulario] = useState<FormularioUsuario | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadFormularios();
  }, [clienteId]);

  const loadFormularios = async () => {
    setLoading(true);
    try {
      const data = await getFormulariosUsuario(clienteId);
      setFormularios(data);
    } catch (error: any) {
      enqueueSnackbar('Error al cargar formularios', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = (formulario: FormularioUsuario) => {
    setSelectedFormulario(formulario);
    setDialogOpen(true);
  };

  const getTipoLabel = (tipo?: string) => {
    switch (tipo) {
      case 'profesor_alumnos':
        return 'Evaluación de Profesor';
      case 'manager_departamento':
        return 'Evaluación de Manager';
      case 'evaluacion_managers':
        return 'Evaluación de Manager';
      default:
        return 'General';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (formularios.length === 0) {
    return (
      <Alert severity="info">
        Este cliente no tiene formularios asociados aún.
      </Alert>
    );
  }

  return (
    <>
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Formulario</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Respondido por</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formularios.map((form) => (
              <TableRow key={form.id}>
                <TableCell>{form.titulo}</TableCell>
                <TableCell>
                  <Chip label={getTipoLabel(form.tipoFormulario)} size="small" />
                </TableCell>
                <TableCell>
                  {form.respuestasAnonimas ? (
                    <Chip label="Anónimo" size="small" color="secondary" />
                  ) : (
                    form.respuestas[0]?.respondidoPor || '-'
                  )}
                </TableCell>
                <TableCell>
                  {form.fecha ? new Date(form.fecha).toLocaleDateString('es-ES') : '-'}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleVerDetalle(form)}
                  >
                    Ver
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedFormulario?.titulo}
          {selectedFormulario?.respuestasAnonimas && (
            <Chip label="Anónimo" size="small" color="secondary" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {selectedFormulario?.respuestas.map((respuesta, idx) => (
              <Box key={idx}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {respuesta.pregunta}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {respuesta.respuesta}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Respondido por: {respuesta.respondidoPor}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Fecha: {new Date(respuesta.fecha).toLocaleDateString('es-ES')}
                  </Typography>
                </Stack>
                {idx < selectedFormulario.respuestas.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

